export interface MultiLineSeries {
  label: string;
  data: number[];
  color: string;
  dim?: boolean;
}

interface Props {
  series: MultiLineSeries[];
  height?: number;
  yMax?: number;
  yMin?: number;
  unit?: string;
  showLegend?: boolean;
}

export function MultiLineChart({
  series,
  height = 160,
  yMax = 100,
  yMin = 0,
  unit = "",
  showLegend = true,
}: Props) {
  const padL = 34;
  const padR = 10;
  const padT = 10;
  const padB = 18;
  const w = 600;
  const h = height;
  const innerW = w - padL - padR;
  const innerH = h - padT - padB;
  const n = series[0]?.data?.length ?? 0;
  const step = innerW / (n - 1 || 1);
  const yScale = (v: number) => padT + innerH - ((v - yMin) / (yMax - yMin)) * innerH;
  const mkPath = (data: number[]) =>
    data
      .map((v, i) => `${i ? "L" : "M"}${(padL + i * step).toFixed(2)},${yScale(v).toFixed(2)}`)
      .join(" ");
  return (
    <div>
      <svg
        viewBox={`0 0 ${w} ${h}`}
        style={{ width: "100%", height, display: "block" }}
        preserveAspectRatio="none"
      >
        {[0, 1, 2, 3, 4].map((i) => {
          const y = padT + (innerH / 4) * i;
          const v = yMax - (yMax / 4) * i;
          return (
            <g key={i}>
              <line x1={padL} x2={w - padR} y1={y} y2={y} stroke="var(--line)" vectorEffect="non-scaling-stroke" />
              <text
                x={padL - 4}
                y={y + 3}
                fontSize="9"
                fill="var(--fg-3)"
                textAnchor="end"
                fontFamily="var(--mono)"
              >
                {Math.round(v)}
                {unit}
              </text>
            </g>
          );
        })}
        {series.map((s, i) => (
          <path
            key={i}
            d={mkPath(s.data)}
            fill="none"
            stroke={s.color}
            strokeWidth="1.25"
            vectorEffect="non-scaling-stroke"
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity={s.dim ? 0.35 : 1}
          />
        ))}
      </svg>
      {showLegend && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 6 }}>
          {series.map((s, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                fontSize: 10,
                color: "var(--fg-2)",
                fontFamily: "var(--mono)",
              }}
            >
              <div style={{ width: 8, height: 2, background: s.color }} />
              {s.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
